import React, { lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { GraduationCap, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui';
import { BrandMark } from '@/components/shared/BrandMark';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { useMotionSafe } from '@/utils/motion';

const GazeHeroCanvas = lazy(() =>
  import('@/components/home/GazeHeroCanvas').then((m) => ({ default: m.GazeHeroCanvas }))
);

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { pageEnter, staggerContainer, staggerItem, hoverLift } = useMotionSafe();

  const features = [
    {
      label: 'Gaze estimation',
      title: 'On-device WebGazer',
      description:
        'Look-and-click calibration trains gaze on the student device — no server ETH-XGaze path.',
    },
    {
      label: 'Pattern review',
      title: 'Behavioral context',
      description: 'Instructors see when attention drifts without crowding the student view.',
    },
    {
      label: 'Data safeguards',
      title: 'Privacy-minded processing',
      description: 'Gaze stays in the browser; the API stores light session events, not video frames.',
    },
    {
      label: 'Live reporting',
      title: 'Instructor oversight',
      description: 'Alert-first session board for who needs intervention now.',
    },
  ];

  return (
    <motion.div
      className="min-h-screen bg-surface-canvas font-sans text-content-primary"
      {...pageEnter}
    >
      <section className="relative isolate min-h-svh overflow-hidden">
        {/* Full-bleed 3D — must not steal clicks from nav/CTAs (eye uses window mousemove) */}
        <div className="pointer-events-none absolute inset-0 z-0">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center bg-surface-nested text-sm text-content-muted">
                Loading preview…
              </div>
            }
          >
            <GazeHeroCanvas fullBleed />
          </Suspense>
        </div>

        {/* Theme-aware veil: opaque left → clear right */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(90deg,var(--color-surface-canvas)_0%,color-mix(in_srgb,var(--color-surface-canvas)_88%,transparent)_38%,color-mix(in_srgb,var(--color-surface-canvas)_35%,transparent)_58%,transparent_78%)] max-lg:bg-[linear-gradient(180deg,transparent_0%,color-mix(in_srgb,var(--color-surface-canvas)_55%,transparent)_42%,var(--color-surface-canvas)_72%)]"
        />

        <nav
          aria-label="Primary navigation"
          className="pointer-events-auto absolute inset-x-0 top-0 z-30 border-b border-subtle/60 bg-surface-canvas/55 backdrop-blur-md"
        >
          <div className="mx-auto flex max-w-page items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <BrandMark />
              <span className="font-ui text-xl font-normal tracking-tight">QuizSecure</span>
            </div>
            <ThemeToggle />
          </div>
        </nav>

        {/* Full-viewport shell is pointer-events-none so it doesn't cover the nav;
            only the copy/CTA column receives clicks. */}
        <div className="pointer-events-none relative z-20 mx-auto flex min-h-svh max-w-page flex-col justify-end px-4 pb-12 pt-24 sm:px-6 lg:justify-center lg:px-8 lg:pb-16 lg:pt-20">
          <div className="pointer-events-auto w-full max-w-xl rounded-card bg-surface-canvas/95 p-5 shadow-paper backdrop-blur-sm lg:max-w-[44%] lg:bg-transparent lg:p-0 lg:shadow-none lg:backdrop-blur-none">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              <motion.p
                variants={staggerItem}
                className="font-display text-lg font-normal tracking-tight text-content-secondary sm:text-xl"
              >
                QuizSecure
              </motion.p>
              <motion.h1
                variants={staggerItem}
                className="mt-4 font-display font-normal leading-[1.08] text-content-primary text-[clamp(2.5rem,5.5vw,4.25rem)]"
              >
                Attention, observed with care.
              </motion.h1>
              {/*
                Plain <p> (not motion) + explicit line break.
                Motion transform + WebGL compositing was ghosting/overlapping this copy.
              */}
              <p
                className="mt-5 max-w-md font-sans text-base text-content-secondary sm:text-lg"
                style={{
                  lineHeight: 1.65,
                  letterSpacing: 'normal',
                  wordSpacing: 'normal',
                  whiteSpace: 'normal',
                  opacity: 1,
                  transform: 'none',
                }}
              >
                Gaze monitoring for fair exams.
                <br />
                Instructors get useful context; students keep calm focus.
              </p>
              <motion.div
                variants={staggerItem}
                className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
              >
                <motion.div {...hoverLift}>
                  <Button
                    size="lg"
                    variant="primary"
                    onClick={() => navigate('/login?role=student')}
                  >
                    <GraduationCap className="size-5" aria-hidden="true" />
                    Student login
                  </Button>
                </motion.div>
                <motion.div {...hoverLift}>
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={() => navigate('/login?role=teacher')}
                  >
                    <LayoutDashboard className="size-5" aria-hidden="true" />
                    Instructor dashboard
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <main>
        <section
          className="mx-auto max-w-page px-4 py-16 sm:px-6 lg:px-8 lg:py-20"
          aria-labelledby="capabilities-heading"
        >
          <div className="max-w-2xl">
            <h2 id="capabilities-heading" className="font-display text-heading font-normal">
              One system for focused, fair examinations
            </h2>
            <p className="mt-3 text-content-secondary">
              Monitoring, analysis, and reporting work together without crowding the exam
              experience.
            </p>
          </div>
          <motion.div
            className="mt-10 grid border-l border-t border-subtle md:grid-cols-2 lg:grid-cols-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >
            {features.map((feature) => (
              <motion.article
                key={feature.title}
                variants={staggerItem}
                {...hoverLift}
                className="border-b border-r border-subtle p-6"
              >
                <div className="mb-5 h-1 w-10 bg-clay" aria-hidden="true" />
                <p className="text-caption font-medium uppercase tracking-[0.16em] text-content-muted">
                  {feature.label}
                </p>
                <h3 className="mt-3 font-display text-heading-sm font-normal">{feature.title}</h3>
                <p className="mt-3 text-sm leading-6 text-content-secondary">
                  {feature.description}
                </p>
              </motion.article>
            ))}
          </motion.div>
        </section>

        <footer className="border-t border-subtle">
          <div className="mx-auto flex max-w-page flex-col gap-5 px-4 py-8 text-sm text-content-muted sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
            <p className="font-ui">QuizSecure — gaze-aware exam monitoring</p>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <a
                href="#"
                className="text-content-secondary underline-offset-4 hover:text-content-primary hover:underline"
              >
                Documentation
              </a>
              <a
                href="#"
                className="text-content-secondary underline-offset-4 hover:text-content-primary hover:underline"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-content-secondary underline-offset-4 hover:text-content-primary hover:underline"
              >
                Support
              </a>
            </div>
          </div>
        </footer>
      </main>
    </motion.div>
  );
};
